import React from "react"
import { observer } from "mobx-react-lite"
import { FieldProps } from "formik"
import without from "lodash/without"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import Templates from "@components/ui/Templates"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import List from "@components/ui/List/List"
import InfiniteCollection from "@components/modals/EditModal/components/BaseContainer/InfiniteCollection"
import { SearchContext } from "@components/prototypes/SearchContext"
import { searchBy } from "@utils/optionsUtils"
import {
  ReportDescriptor,
  reportOptions,
} from "@framework/types/workbook-report"
import { getReportDescriptionByType } from "@framework/constants/workbook"

import styles from "./QueryTypeSelect.module.sass"

const allOptions: ReportDescriptor[] = [...reportOptions].map((it) => {
  return getReportDescriptionByType(it)
})

export interface QueryTypeSelectProps extends FieldProps<string[]> {
  label?: string
}

const QueryTypeSelect: React.FC<QueryTypeSelectProps> = observer(
  ({ label, field, form }) => {
    const context = React.useContext(SearchContext)

    const [open, setOpen] = React.useState(false)
    const [options, setOptions] = React.useState<ReportDescriptor[]>([])

    const { value = [], name } = field
    const { setFieldValue } = form

    const handleClick = (newValue: string) => {
      if (!value.includes(newValue)) {
        setFieldValue(name, [...value, newValue])
      } else {
        setFieldValue(name, without(value, newValue))
      }
    }

    const renderRow = (dataType: ReportDescriptor) => {
      const active = value?.includes(dataType.type)

      return (
        <ListItem active={active} onClick={() => handleClick(dataType.type)}>
          <List direction="row" gutter="8" align="center">
            <DocumentIcon icon={dataType.icon ?? "global"} size="medium" />
            {dataType.displayName}
          </List>
        </ListItem>
      )
    }

    const isChecked = !!value?.length
    const isLoading = false

    React.useEffect(() => {
      if (!context?.query) setOptions([...allOptions])

      setOptions(searchBy(allOptions, context?.query, (it) => it.type))
    }, [context?.query])

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {options.length === 0 ? (
              isLoading ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Query Types found</NotFound>
              )
            ) : (
              <InfiniteCollection<ReportDescriptor>
                total={options.length}
                getItem={(idx) => options.at(idx) ?? null}
                renderItem={renderRow}
                minRowHeight={42}
              />
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  }
)

export default QueryTypeSelect
